/**
 * Model Scoreboard - Compare on-prem and cloud models with benchmark data
 */

import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Check, Cloud, Server, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { ModelScore } from '@/lib/modelScoring';
import type { AiServiceModelPreset } from '@/lib/aiServiceModels';
import type { WizardPersona, WizardEnvironment } from '@/stores/wizardStore';
import { explainModelFit } from '@/lib/scoring/explainer';

interface ModelWithDetails extends ModelScore {
  display_name: string;
  params_b: number;
  family: string;
}

interface ModelScoreboardProps {
  onPremModels: ModelWithDetails[];
  cloudModels: AiServiceModelPreset[];
  selectedModelIds: string[];
  onSelectModel: (modelId: string) => void;
  personas: WizardPersona[];
  environment: WizardEnvironment;
}

interface ScoredModel {
  id: string;
  name: string;
  type: 'on-prem' | 'cloud';
  score: number;
  mmlu?: number;
  humaneval?: number;
  costPer1M?: string;
  isSelected?: boolean;
  model?: ModelWithDetails;
  cloudPreset?: AiServiceModelPreset;
}

export function ModelScoreboard({
  onPremModels,
  cloudModels,
  selectedModelIds,
  onSelectModel,
  personas,
  environment,
}: ModelScoreboardProps) {
  const [expandedModelId, setExpandedModelId] = useState<string | null>(null);
  // Calculate scores for cloud models based on same logic
  const scoredCloudModels: ScoredModel[] = cloudModels
    .filter(cm => cm.benchmarks)
    .map(cm => {
      const benchmarks = cm.benchmarks!;
      
      // Calculate weighted score based on personas
      const weights = getPersonaWeights(personas);
      let score = 0;
      let totalWeight = 0;

      if (benchmarks.mmlu && weights.mmlu > 0) {
        score += (benchmarks.mmlu / 88.7) * 100 * weights.mmlu;
        totalWeight += weights.mmlu;
      }
      if (benchmarks.humaneval && weights.humaneval > 0) {
        score += (benchmarks.humaneval / 92.0) * 100 * weights.humaneval;
        totalWeight += weights.humaneval;
      }
      if (benchmarks.arena_elo && weights.arena_elo > 0) {
        score += (benchmarks.arena_elo / 1310) * 100 * weights.arena_elo;
        totalWeight += weights.arena_elo;
      }

      const normalizedScore = totalWeight > 0 ? score / totalWeight : 50;

      return {
        id: cm.value,
        name: cm.label,
        type: 'cloud' as const,
        score: normalizedScore,
        mmlu: benchmarks.mmlu,
        humaneval: benchmarks.humaneval,
        costPer1M: cm.inputCost ? `$${(cm.inputCost + (cm.outputCost || 0)).toFixed(2)}` : undefined,
        cloudPreset: cm,
      };
    });

  // Map on-prem models
  const scoredOnPremModels: ScoredModel[] = onPremModels.map(model => ({
    id: model.modelId,
    name: model.display_name,
    type: 'on-prem' as const,
    score: model.score,
    mmlu: model.benchmarkBreakdown?.mmlu,
    humaneval: model.benchmarkBreakdown?.humaneval,
    costPer1M: 'Included',
    isSelected: selectedModelIds.includes(model.modelId),
    model,
  }));

  // Combine and sort by score
  const allModels = [...scoredOnPremModels, ...scoredCloudModels].sort((a, b) => b.score - a.score);

  // Find top cloud model score
  const topCloudScore = Math.max(...scoredCloudModels.map(m => m.score), 0);
  const competitiveThreshold = topCloudScore * 0.9; // Within 10%

  if (allModels.length === 0) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">
          No benchmark data available. Please add personas to see recommendations.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Model Performance Comparison</h3>
          <p className="text-sm text-muted-foreground">
            On-prem models compared with cloud alternatives
          </p>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon">
                <Info className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-sm">
                Fit Score is calculated based on your persona requirements, weighted by benchmark performance.
                Green highlights indicate on-prem models within 10% of the top cloud model.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr className="border-b">
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Rank</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Model</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Deployment</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Fit Score</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">MMLU</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">HumanEval</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Cost/1M</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Action</th>
              </tr>
            </thead>
            <tbody>
              {allModels.map((model, index) => {
                const isTopCloud = model.type === 'cloud' && model.score === topCloudScore;
                const isCompetitive = model.type === 'on-prem' && model.score >= competitiveThreshold;
                const rowClass = isTopCloud
                  ? 'bg-blue-50 dark:bg-blue-950/20 border-l-4 border-l-blue-500'
                  : isCompetitive
                  ? 'bg-green-50 dark:bg-green-950/20 border-l-4 border-l-green-500'
                  : model.isSelected
                  ? 'bg-primary/5 border-l-4 border-l-primary'
                  : '';

                const isExpanded = expandedModelId === model.id;
                
                // Generate explanations
                const explanation = model.model ? explainModelFit({
                  modelScore: model.model,
                  personas,
                  environment,
                  modelDetails: {
                    display_name: model.name,
                    params_b: model.model.params_b,
                    isOnPrem: model.type === 'on-prem',
                  },
                  topCloudScore,
                }) : null;

                return (
                  <>
                    <tr 
                      key={model.id} 
                      className={`border-b hover:bg-muted/30 transition-colors ${rowClass} ${isExpanded ? 'border-b-0' : ''}`}
                    >
                    <td className="px-4 py-3 text-sm font-medium">{index + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => setExpandedModelId(isExpanded ? null : model.id)}
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                        <div>
                          <div className="font-medium text-sm">{model.name}</div>
                          {model.model && (
                            <div className="text-xs text-muted-foreground">{model.model.family}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={model.type === 'on-prem' ? 'default' : 'secondary'} className="gap-1">
                        {model.type === 'on-prem' ? (
                          <>
                            <Server className="h-3 w-3" />
                            On-Prem
                          </>
                        ) : (
                          <>
                            <Cloud className="h-3 w-3" />
                            Cloud
                          </>
                        )}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Progress value={model.score} className="h-2 w-20" />
                        <span className="text-sm font-medium min-w-[3ch]">{Math.round(model.score)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {model.mmlu ? (
                        <div className="flex items-center gap-2">
                          <Progress value={(model.mmlu / 88.7) * 100} className="h-1.5 w-12" />
                          <span className="text-sm">{model.mmlu.toFixed(1)}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {model.humaneval ? (
                        <div className="flex items-center gap-2">
                          <Progress value={(model.humaneval / 92.0) * 100} className="h-1.5 w-12" />
                          <span className="text-sm">{model.humaneval.toFixed(1)}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium">{model.costPer1M || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      {model.type === 'on-prem' ? (
                        <Button
                          size="sm"
                          variant={model.isSelected ? 'default' : 'outline'}
                          onClick={() => onSelectModel(model.id)}
                          className="gap-1"
                        >
                          {model.isSelected ? (
                            <>
                              <Check className="h-3 w-3" />
                              Selected
                            </>
                          ) : (
                            'Select'
                          )}
                        </Button>
                      ) : (
                        <Badge variant="outline" className="text-xs">
                          Reference
                        </Badge>
                      )}
                    </td>
                  </tr>
                  
                  {/* Expandable explanation row */}
                  {isExpanded && explanation && (
                    <tr key={`${model.id}-expanded`} className={`border-b ${rowClass}`}>
                      <td colSpan={8} className="px-4 py-4 bg-muted/20">
                        <div className="space-y-4 max-w-4xl">
                          {/* Highlights */}
                          {explanation.highlights.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold mb-2 text-green-600 dark:text-green-400">
                                ✓ Why This Model?
                              </h4>
                              <ul className="space-y-1.5 ml-4">
                                {explanation.highlights.map((item, i) => (
                                  <li key={i} className="text-sm text-foreground/90 flex items-start gap-2">
                                    <span className="text-green-500 mt-0.5">•</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Persona Fit */}
                          {explanation.personaFit.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold mb-2 text-blue-600 dark:text-blue-400">
                                🎯 Best For
                              </h4>
                              <ul className="space-y-1.5 ml-4">
                                {explanation.personaFit.map((item, i) => (
                                  <li key={i} className="text-sm text-foreground/90 flex items-start gap-2">
                                    <span className="text-blue-500 mt-0.5">•</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Considerations */}
                          {explanation.considerations.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold mb-2 text-amber-600 dark:text-amber-400">
                                ⚠️ Considerations
                              </h4>
                              <ul className="space-y-1.5 ml-4">
                                {explanation.considerations.map((item, i) => (
                                  <li key={i} className="text-sm text-foreground/90 flex items-start gap-2">
                                    <span className="text-amber-500 mt-0.5">•</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {scoredOnPremModels.some(m => m.score >= competitiveThreshold) && (
        <Card className="p-4 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
          <p className="text-sm text-green-900 dark:text-green-100">
            <strong>Great news!</strong> Your on-prem options perform within 10% of top cloud models,
            offering similar capabilities with full data control and predictable costs.
          </p>
        </Card>
      )}
    </div>
  );
}

// Helper function to calculate weights based on personas
function getPersonaWeights(personas: WizardPersona[]): { mmlu: number; humaneval: number; arena_elo: number } {
  const expertiseWeights: Record<string, { mmlu: number; humaneval: number; arena_elo: number }> = {
    coding: { humaneval: 0.6, mmlu: 0.4, arena_elo: 0 },
    thinking: { mmlu: 0.7, humaneval: 0, arena_elo: 0.3 },
    'long-conversation': { arena_elo: 0.7, mmlu: 0.3, humaneval: 0 },
    summarization: { arena_elo: 0.6, mmlu: 0.4, humaneval: 0 },
    general: { arena_elo: 1.0, mmlu: 0, humaneval: 0 },
  };

  if (personas.length === 0) {
    return expertiseWeights.general;
  }

  const aggregated = { mmlu: 0, humaneval: 0, arena_elo: 0 };

  personas.forEach(persona => {
    const weight = 1 / personas.length;
    const expertiseType = persona.expertiseType || 'general';
    const weights = expertiseWeights[expertiseType] || expertiseWeights.general;

    aggregated.mmlu += weights.mmlu * weight;
    aggregated.humaneval += weights.humaneval * weight;
    aggregated.arena_elo += weights.arena_elo * weight;
  });

  return aggregated;
}
